import React, { useState, useEffect } from "react";
import { Brain, Plus, Trash2, Lock, Loader2 } from "lucide-react";
import { getMemory, addMemory, deleteMemory } from "../../lib/api";
import { SectionHeader } from "./Sections";
import dayjs from "dayjs";

const SENS_TONE = { confidential: "#F43F5E", restricted: "#F59E0B", normal: "#10B981" };

export default function MemoryPanel() {
  const [mems, setMems] = useState([]);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Professional preferences");
  const [sensitivity, setSensitivity] = useState("normal");
  const [busy, setBusy] = useState(false);

  const load = () => getMemory().then(setMems).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!content.trim()) return;
    setBusy(true);
    await addMemory({ category, content, sensitivity, permission_scope: "all_agents" });
    setContent("");
    await load();
    setBusy(false);
  };
  const remove = async (id) => { await deleteMemory(id); load(); };

  const grouped = mems.reduce((acc, m) => { (acc[m.category] = acc[m.category] || []).push(m); return acc; }, {});

  return (
    <div>
      <SectionHeader overline="Executive Memory & Context Layer" title="What I Know About You"
        desc="Permissioned, editable and auditable. Sensitive items carry stricter access than ordinary preferences." />

      <div className="glass-card p-5 mb-6 fade-up">
        <div className="overline mb-3 flex items-center gap-1.5"><Plus size={13} /> Remember this</div>
        <div className="grid sm:grid-cols-3 gap-2 mb-2">
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category"
            className="bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50" data-testid="memory-category" />
          <select value={sensitivity} onChange={(e) => setSensitivity(e.target.value)}
            className="bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50" data-testid="memory-sensitivity">
            <option value="normal">Normal</option>
            <option value="restricted">Restricted</option>
            <option value="confidential">Confidential</option>
          </select>
          <button onClick={add} disabled={busy} className="gold-btn rounded-lg px-4 py-2 text-sm flex items-center justify-center gap-2" data-testid="memory-add">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Store
          </button>
        </div>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={2} placeholder="e.g. I prefer one-line recommendations at the top of every brief"
          className="w-full bg-[#0F131C] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-amber-500/50" data-testid="memory-content" />
      </div>

      <div className="space-y-5">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <div className="overline mb-2 flex items-center gap-1.5"><Brain size={13} /> {cat}</div>
            <div className="space-y-2">
              {items.map((m) => (
                <div key={m.id} className="glass-card p-4 flex items-start justify-between gap-3 fade-up" data-testid="memory-item">
                  <div className="flex-1">
                    <p className="text-sm text-slate-200">{m.content}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1" style={{ color: SENS_TONE[m.sensitivity] }}>
                        <Lock size={10} /> {m.sensitivity}
                      </span>
                      <span>scope: {m.permission_scope}</span>
                      <span>source: {m.source}</span>
                      <span>verified {dayjs(m.last_verified).format("MMM D")}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(m.id)} className="p-2 rounded-lg hover:bg-rose-500/15 hover:text-rose-400 transition text-slate-500" data-testid="memory-delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
